import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  clearCodeLibraryComparisonRating,
  submitCodeLibraryComparisonFeedback,
  type ComparisonFeedbackState,
  type ComparisonJob,
} from "@/api/code_library_compare";
import FeedbackActions from "@/components/FeedbackActions";
import FeedbackDialog from "@/components/FeedbackDialog";
import { comparisonQueryKey } from "@/context/CodeLibraryCompareContext";
import { useToast } from "@/hooks/useToast";
import type { FeedbackEntryPoint } from "@/lib/responseFeedback";

const EMPTY_STATE: ComparisonFeedbackState = { thumb_state: null, has_feedback: false };

/** Research Assistant-style Helpful, Unhelpful, and Feedback controls for a whole comparison. */
export function ComparisonFeedback({ comparisonId, feedbackState }: { comparisonId: number; feedbackState?: ComparisonFeedbackState }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [dialogEntryPoint, setDialogEntryPoint] = useState<FeedbackEntryPoint | null>(null);
  // Each open gets a fresh dialog. A save still pending from an earlier, cancelled dialog closes
  // only that dialog, never one opened after it.
  const dialogSession = useRef(0);
  const openDialog = (entryPoint: FeedbackEntryPoint) => {
    dialogSession.current += 1;
    setDialogEntryPoint(entryPoint);
  };
  const session = dialogSession.current;
  const [state, setState] = useState(feedbackState ?? EMPTY_STATE);
  // Rating buttons are disabled while a rating saves, so two ratings never race each other.
  const [ratingSaving, setRatingSaving] = useState(false);

  useEffect(() => setState(feedbackState ?? EMPTY_STATE), [feedbackState]);

  // Each response updates only the field its request changed, so a slow rating response cannot
  // overwrite feedback saved while it was in flight. The cached comparison is updated too, so
  // reopening it from history shows the saved state.
  const applyState = (next: Partial<ComparisonFeedbackState>) => {
    setState((current) => ({ ...current, ...next }));
    queryClient.setQueryData<ComparisonJob>(comparisonQueryKey(comparisonId), (job) =>
      job ? { ...job, feedback_state: { ...(job.feedback_state ?? EMPTY_STATE), ...next } } : job,
    );
  };

  const updateRating = async (request: () => Promise<ComparisonFeedbackState>) => {
    setRatingSaving(true);
    try {
      const { thumb_state } = await request();
      applyState({ thumb_state });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const HB: any = (globalThis as any).Honeybadger;
      HB?.notify?.(error);
      toast({
        title: "Your rating was not saved",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRatingSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        <span>Was this comparison helpful?</span>
        <FeedbackActions
          subject="comparison"
          className="ml-0"
          isHelpfulClicked={state.thumb_state === "helpful"}
          isUnhelpfulClicked={state.thumb_state === "unhelpful"}
          hasFeedbackSubmission={state.has_feedback}
          ratingDisabled={ratingSaving}
          onHelpful={() =>
            updateRating(() =>
              state.thumb_state === "helpful"
                ? clearCodeLibraryComparisonRating(comparisonId)
                : submitCodeLibraryComparisonFeedback(comparisonId, { entryPoint: "thumbs_up" }),
            )
          }
          onUnhelpful={() => {
            if (state.thumb_state === "unhelpful") {
              updateRating(() => clearCodeLibraryComparisonRating(comparisonId));
            } else {
              openDialog("thumbs_down");
            }
          }}
          onFeedback={() => openDialog("feedback")}
        />
      </div>
      <FeedbackDialog
        key={session}
        open={dialogEntryPoint != null}
        onOpenChange={(open) => !open && session === dialogSession.current && setDialogEntryPoint(null)}
        entryPoint={dialogEntryPoint ?? "feedback"}
        onSubmit={async ({ reasonCode, detail }) => {
          const entryPoint = dialogEntryPoint ?? "feedback";
          // An Unhelpful submission is a rating too, so the rating buttons stay disabled until it
          // settles, even if the dialog is cancelled meanwhile.
          const isRating = entryPoint === "thumbs_down";
          if (isRating) setRatingSaving(true);
          try {
            const { thumb_state, has_feedback } = await submitCodeLibraryComparisonFeedback(comparisonId, {
              entryPoint,
              reasonCode,
              detail,
            });
            applyState(isRating ? { thumb_state } : { has_feedback });
          } finally {
            if (isRating) setRatingSaving(false);
          }
        }}
      />
    </>
  );
}
